import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Toaster } from "@/components/ui/sonner";
import { api, fmtSigned, fmtUsd } from "@/lib/journal";
import { CurrencySwitcher } from "@/components/CurrencySwitcher";
import { ArrowLeft, TrendUp, TrendDown } from "@phosphor-icons/react";

const CallCard = ({ c, tone, index }) => (
  <div
    data-testid={`${tone}-call-${index}`}
    className="border border-[#1F1F1F] bg-[#080808] p-5 fade-up"
    style={{ animationDelay: `${index * 80}ms` }}
  >
    <div className="flex items-baseline justify-between">
      <span className="font-mono text-sm text-white">{c.asset}</span>
      <span className={`font-mono text-sm ${tone === "best" ? "text-[#4ADE80]" : "text-[#EF4444]"}`}>
        {fmtSigned(c.pnl)}
        {c.pnl_pct != null && <span className="ml-2 text-[11px] opacity-70">{c.pnl_pct > 0 ? "+" : ""}{c.pnl_pct}%</span>}
      </span>
    </div>
    <p className="mt-1 font-mono text-[10px] uppercase tracking-wider text-zinc-600">
      {c.portfolio} · {c.date}
    </p>
    {(c.closing_notes || c.reason) && (
      <p className="mt-3 line-clamp-3 text-xs text-zinc-400">{c.closing_notes || c.reason}</p>
    )}
  </div>
);

export default function YearlyReview() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [fxTick, setFxTick] = useState(0);
  const bumpFx = useCallback(() => setFxTick((t) => t + 1), []);
  const year = params.get("year") || "";

  useEffect(() => {
    api.get("/reports/yearly", { params: year ? { year } : {} })
      .then(({ data }) => setData(data))
      .catch(() => setData(null));
  }, [year]);

  const months = useMemo(() => data?.months || [], [data]);
  const t = data?.totals;

  return (
    <div className="grain min-h-screen bg-[#050505]">
      <Toaster position="top-right" theme="dark" />

      <header className="sticky top-0 z-20 border-b border-[#1F1F1F] bg-black/60 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-y-3 px-4 py-4 md:px-8">
          <div className="flex items-center gap-3">
            <div className="h-6 w-[3px] shrink-0 bg-[#C58361]" />
            <div>
              <h1 className="font-display text-base font-medium tracking-tight text-white md:text-lg">
                TJ13{" "}
                <span className="text-[#C58361]">
                  by{" "}
                  <a
                    data-testid="alvo13-link"
                    href="https://alvo-13.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline decoration-[#C58361]/40 decoration-1 underline-offset-4 transition-colors hover:text-[#E0A27F] hover:decoration-[#E0A27F]"
                  >
                    ALVO 13
                  </a>
                </span>
              </h1>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-600">
                Monthly PnL & standout calls
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CurrencySwitcher onChanged={bumpFx} />
            {data?.available_years?.length > 0 && (
              <Select value={String(data.year)} onValueChange={(v) => setParams({ year: v })}>
                <SelectTrigger data-testid="review-year-select" className="h-9 w-[110px] rounded-none border-[#1F1F1F] bg-[#050505] text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none border-[#1F1F1F] bg-[#0A0A0A]">
                  {data.available_years.map((y) => (
                    <SelectItem key={y} value={String(y)} data-testid={`review-year-${y}`}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button
              data-testid="review-back-btn"
              onClick={() => navigate("/")}
              className="h-9 rounded-none border border-[#1F1F1F] bg-transparent px-3 text-xs text-zinc-300 hover:bg-[#111111] hover:text-white"
            >
              <ArrowLeft size={14} className="mr-2" /> Back
            </Button>
          </div>
        </div>
      </header>

      <main key={fxTick} className="relative z-[2] mx-auto max-w-7xl px-4 py-10 md:px-8 md:py-12">
        <div className="mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#C58361]">Annual Performance</p>
          <h2 className="mt-3 font-display text-4xl font-medium tracking-tighter text-white sm:text-5xl" data-testid="review-year-heading">
            {data?.year || "—"}
          </h2>
        </div>

        {!data ? (
          <p className="font-mono text-sm text-zinc-600">Loading review…</p>
        ) : (
          <div className="space-y-10">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {[
                ["Realized PnL", fmtSigned(t.realized_pnl), t.realized_pnl >= 0],
                ["Positions Closed", String(t.closed), null],
                ["Win Rate", `${t.win_rate}%`, null],
                ["Best / Worst Month", `${t.best_month || "—"} / ${t.worst_month || "—"}`, null],
              ].map(([label, value, pos], i) => (
                <div key={label}
                     data-testid={`review-metric-${i}`}
                     className="border border-[#1F1F1F] bg-surface p-6 fade-up"
                     style={{ animationDelay: `${i * 40}ms` }}>
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">{label}</span>
                  <div className={`mt-5 font-mono text-2xl tracking-tight ${
                    pos === null ? "text-white" : pos ? "text-[#4ADE80]" : "text-[#EF4444]"
                  }`}>
                    {value}
                  </div>
                </div>
              ))}
            </div>

            <div className="border border-[#1F1F1F] bg-surface p-6" data-testid="review-monthly-chart">
              <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Monthly Realized PnL</h3>
              <div className="mt-6 h-[260px] min-h-[260px] w-full">
                <ResponsiveContainer width="100%" height={260} minWidth={0} minHeight={260}>
                  <BarChart data={months} margin={{ left: -16, right: 8, top: 8, bottom: 0 }}>
                    <CartesianGrid stroke="#1F1F1F" vertical={false} />
                    <XAxis dataKey="month" stroke="#52525B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={{ stroke: "#1F1F1F" }} />
                    <YAxis stroke="#52525B" tick={{ fontSize: 11, fontFamily: "JetBrains Mono" }} tickLine={false} axisLine={false} />
                    <Tooltip
                      formatter={(v) => fmtUsd(v)}
                      contentStyle={{ background: "#0A0A0A", border: "1px solid #1F1F1F", borderRadius: 2, fontFamily: "JetBrains Mono", fontSize: 12 }}
                      labelStyle={{ color: "#71717A" }}
                    />
                    <Bar dataKey="pnl" radius={[1, 1, 0, 0]}>
                      {months.map((m) => (
                        <Cell key={m.month} fill={m.pnl >= 0 ? "#C58361" : "#EF4444"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <div className="mb-4 flex items-center gap-2">
                  <TrendUp size={14} className="text-[#4ADE80]" />
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Best Calls</h3>
                </div>
                <div className="space-y-3" data-testid="review-best-calls">
                  {data.best_calls.length === 0
                    ? <p className="border border-dashed border-[#1F1F1F] px-4 py-10 text-center text-sm text-zinc-600">No closed trades this year.</p>
                    : data.best_calls.map((c, i) => <CallCard key={i} c={c} tone="best" index={i} />)}
                </div>
              </div>
              <div>
                <div className="mb-4 flex items-center gap-2">
                  <TrendDown size={14} className="text-[#EF4444]" />
                  <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Worst Calls</h3>
                </div>
                <div className="space-y-3" data-testid="review-worst-calls">
                  {data.worst_calls.length === 0
                    ? <p className="border border-dashed border-[#1F1F1F] px-4 py-10 text-center text-sm text-zinc-600">No losing trades this year.</p>
                    : data.worst_calls.map((c, i) => <CallCard key={i} c={c} tone="worst" index={i} />)}
                </div>
              </div>
            </div>

            {data.by_asset.length > 0 && (
              <div className="border border-[#1F1F1F] bg-surface p-6" data-testid="review-by-asset">
                <h3 className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-500">Realized PnL by Asset</h3>
                <div className="mt-6 space-y-3">
                  {data.by_asset.map((a) => (
                    <div key={a.asset} className="flex items-center justify-between border-b border-[#1F1F1F] pb-3">
                      <span className="font-mono text-sm text-zinc-300">{a.asset}</span>
                      <span className={`font-mono text-sm ${a.pnl >= 0 ? "text-[#4ADE80]" : "text-[#EF4444]"}`}>
                        {fmtSigned(a.pnl)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
